import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "capitaly_token";

export const getStoredToken = () => {
    try {
        return localStorage.getItem(TOKEN_KEY);
    } catch (err) {
        // localStorage may throw in private mode or when disabled
        if (process.env.NODE_ENV !== "production") {
            console.warn("[api] localStorage.getItem failed:", err?.message);
        }
        return null;
    }
};
export const setStoredToken = (tok) => {
    try {
        if (tok) localStorage.setItem(TOKEN_KEY, tok);
        else localStorage.removeItem(TOKEN_KEY);
    } catch (err) {
        if (process.env.NODE_ENV !== "production") {
            console.warn("[api] localStorage write failed:", err?.message);
        }
    }
};

const client = axios.create({ baseURL: API, timeout: 60000 });

client.interceptors.request.use((config) => {
    const tok = getStoredToken();
    if (tok) config.headers.Authorization = `Bearer ${tok}`;
    return config;
});

// Generic 401 → drop token (let AuthContext detect and redirect)
client.interceptors.response.use(
    (r) => r,
    (err) => {
        if (err?.response?.status === 401) {
            // Don't auto-clear on /auth/me probe — that's expected when not logged in
            const url = err.config?.url || "";
            if (!url.includes("/auth/me")) {
                // 401 on a real endpoint → token likely expired
                setStoredToken(null);
            }
        }
        return Promise.reject(err);
    }
);

// ---------------------- Auth ----------------------
export const authRegister = (payload) => client.post("/auth/register", payload).then(r => r.data);
export const authLogin = (payload) => client.post("/auth/login", payload).then(r => r.data);
export const authLogout = () => client.post("/auth/logout").then(r => r.data);
export const authMe = () => client.get("/auth/me").then(r => r.data);
export const authUpdateMe = (payload) => client.patch("/auth/me", payload).then(r => r.data);
export const authGoogleSession = (sessionId) =>
    client.post("/auth/google/session", null, { headers: { "X-Session-ID": sessionId } }).then(r => r.data);

// ---------------------- Data endpoints ----------------------
export const getCategories = () => client.get("/categories").then(r => r.data);
export const getMarket = (category) => client.get(`/markets/${category}`).then(r => r.data);
export const getAsset = (symbol) => client.get(`/asset/${encodeURIComponent(symbol)}`).then(r => r.data);
export const getChart = (symbol, period = "6mo", interval = "1d") =>
    client.get(`/asset/${encodeURIComponent(symbol)}/chart`, { params: { period, interval } }).then(r => r.data);
export const getAssetPriceAt = (symbol, date) =>
    client.get(`/asset/${encodeURIComponent(symbol)}/price-at`, { params: { date } }).then(r => r.data);
export const getMacro = () => client.get("/macro").then(r => r.data);
export const getAnalyst = (symbol) => client.get(`/analyst/${encodeURIComponent(symbol)}`).then(r => r.data);
export const getOpportunities = (params = {}) => client.get("/opportunities", { params }).then(r => r.data);
export const getOpportunitiesTopBySide = (params = {}) => client.get("/opportunities/top-by-side", { params }).then(r => r.data);
export const getOpportunitiesMeta = () => client.get("/opportunities/meta").then(r => r.data);
export const getExpertAnalyses = () => client.get("/opportunities/expert-analyses").then(r => r.data);
export const triggerExpertCycle = () => client.post("/admin/expert-analyses/trigger").then(r => r.data);
export const getExpertSuggestions = () => client.get("/expert-layer/suggestions").then(r => r.data);
export const acceptExpertSuggestion = (id) => client.post(`/admin/expert-layer/suggestions/${id}/accept`).then(r => r.data);
export const dismissExpertSuggestion = (id) => client.post(`/admin/expert-layer/suggestions/${id}/dismiss`).then(r => r.data);
export const triggerLearningCheck = () => client.post("/admin/expert-layer/learning-check/trigger").then(r => r.data);
export const toggleExpertAutotrade = () => client.post("/admin/expert-layer/autotrade/toggle").then(r => r.data);

// Admin-only visibility into expert_auto trades (bypasses per-user scoping)
export const adminGetExpertOpenTrades   = () => client.get("/admin/expert-trades/open").then(r => r.data);
export const adminGetExpertPendingOrders = () => client.get("/admin/expert-trades/pending").then(r => r.data);
export const adminGetExpertClosedTrades  = (limit = 500) => client.get("/admin/expert-trades/closed", { params: { limit } }).then(r => r.data);
export const adminGetExpertStats         = () => client.get("/admin/expert-trades/stats").then(r => r.data);
export const adminGetExpertEdgeBuckets   = () => client.get("/admin/expert-trades/edge-buckets").then(r => r.data);

// Section 3 Part B — Exclusion rules CRUD (admin-only)
export const listExclusionRules   = (activeOnly = false) => client.get("/admin/expert-layer/exclusion-rules", { params: { active_only: activeOnly } }).then(r => r.data);
export const toggleExclusionRule  = (id, active) => client.post(`/admin/expert-layer/exclusion-rules/${id}/toggle`, null, { params: { active } }).then(r => r.data);
export const deleteExclusionRule  = (id) => client.delete(`/admin/expert-layer/exclusion-rules/${id}`).then(r => r.data);
export const getWatchlist = () => client.get("/watchlist").then(r => r.data);
export const addWatchlist = (payload) => client.post("/watchlist", payload).then(r => r.data);
export const removeWatchlist = (symbol) => client.delete(`/watchlist/${encodeURIComponent(symbol)}`).then(r => r.data);
export const getPortfolio = () => client.get("/portfolio").then(r => r.data);
export const addPosition = (payload) => client.post("/portfolio", payload).then(r => r.data);
export const updatePosition = (id, payload) => client.patch(`/portfolio/${encodeURIComponent(id)}`, payload).then(r => r.data);
export const swapPosition = (payload) => client.post("/portfolio/swap", payload).then(r => r.data);
export const getSwapHistory = () => client.get("/portfolio/swap/history").then(r => r.data);

// Open Trades (Position Management)
export const listOpenTrades = () => client.get("/open-trades").then(r => r.data);
export const createOpenTrade = (payload) => client.post("/open-trades", payload).then(r => r.data);
export const updateOpenTrade = (id, payload) => client.patch(`/open-trades/${id}`, payload).then(r => r.data);
export const closeOpenTrade = (id) => client.delete(`/open-trades/${id}`).then(r => r.data);
export const refreshOpenTrade = (id) => client.post(`/open-trades/${id}/refresh`).then(r => r.data);
// Pending Orders (limit / stop orders)
export const listPendingOrders = () => client.get("/pending-orders").then(r => r.data);
export const createPendingOrder = (payload) => client.post("/pending-orders", payload).then(r => r.data);
export const updatePendingOrder = (id, payload) => client.patch(`/pending-orders/${id}`, payload).then(r => r.data);
export const cancelPendingOrder = (id) => client.delete(`/pending-orders/${id}`).then(r => r.data);
// Closed Positions + Edge Analytics
export const listClosedPositions = () => client.get("/closed-positions").then(r => r.data);
export const getEdgeAnalytics = (filter = "all") => client.get("/closed-positions/edge", { params: { filter } }).then(r => r.data);
export const deleteClosedPosition = (id) => client.delete(`/closed-positions/${id}`).then(r => r.data);
export const updateClosedPosition = (id, payload) => client.patch(`/closed-positions/${id}`, payload).then(r => r.data);
export const getClosedTradesStats = () => client.get("/closed-trades/stats").then(r => r.data);
export const getClosedTradesEdgeBuckets = () => client.get("/closed-trades/edge-buckets").then(r => r.data);

// On-demand section explanation (asset page). Passes an AbortSignal so the
// caller can cancel a stale request when a different section is opened.
export const getSectionExplanation = (payload, signal) =>
    client.post("/analysis/section-explanation", payload, { signal }).then(r => r.data);
export const removePosition = (id) => client.delete(`/portfolio/${encodeURIComponent(id)}`).then(r => r.data);
export const getPortfolioHistory = (interval = "1d") => client.get("/portfolio/history", { params: { interval } }).then(r => r.data);
export const getFx = () => client.get("/fx").then(r => r.data);
export const getBudgetCategories = () => client.get("/budget/categories").then(r => r.data);
export const getBudget = (month) => client.get("/budget", { params: month ? { month } : {} }).then(r => r.data);
export const addBudgetTx = (payload) => client.post("/budget", payload).then(r => r.data);
export const removeBudgetTx = (id) => client.delete(`/budget/${encodeURIComponent(id)}`).then(r => r.data);

export function formatApiErrorDetail(detail) {
    if (detail == null) return null;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" · ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}
